import React from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.js";
import { applyThemePreference, readStoredThemePreference } from "./lib/theme.js";
import { installKeyboardReveal } from "./lib/keyboardReveal.js";
import { initializeStartupTelemetry, startupError } from "./lib/startupTelemetry.js";
import "./styles.css";

initializeStartupTelemetry();
applyThemePreference(readStoredThemePreference());
installKeyboardReveal();

/*
 * The offline shell is a PRODUCTION worker, and "am I a dev server" is a
 * property of the build, not of a port number.
 *
 * This used to read `window.location.port !== "5173"`, which named the owner's
 * own `npm run dev` and nothing else. Every other dev server registered the
 * real `sw.js` against Vite: worktree dev servers (3399/5273 and up), the
 * disposable dev VM, and — the expensive one — **every Playwright run**, which
 * takes its ports from `PLAYWRIGHT_WEB_PORT` and has never once been 5173.
 *
 * What that put in front of the browser is not a caching nicety. `sw.js`
 * answers `event.request.mode === "navigate"` from a cached shell, caches
 * `/api/v1` responses, and re-issues every asset fetch it intercepts. Against
 * a dev server whose module graph is transformed per request and re-keyed on
 * every edit, that is a second, stale origin sitting between the test and the
 * app — and it produced exactly the symptoms the e2e suite has been re-filing
 * for months under four different issue numbers: `page.goto` hanging until the
 * test timeout, `page.reload: net::ERR_FAILED`, `read ECONNRESET` on a proxied
 * API call, and a shell that paints with an empty `<main>` because the chunk
 * under it came from a cache written by an earlier test. Which tests lost
 * depended on which navigation the worker happened to be claiming, which is why
 * the red set moved between runs of the same commit (#1393, #1423, #1357,
 * #1538, #1545).
 *
 * `import.meta.env.DEV` is the predicate that was meant all along: Vite sets it
 * true for `vite dev` on any port and false in the production bundle the API
 * serves out of `WEB_DIST_DIR`. Production behaviour is unchanged.
 */
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    if (!import.meta.env.DEV) {
      navigator.serviceWorker.register("/sw.js").catch(() => startupError("worker_failed"));
      return;
    }

    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
      .then(() => window.caches.keys())
      .then((keys) =>
        Promise.all(keys.filter((key) => key.startsWith("household-shell-")).map((key) => window.caches.delete(key)))
      )
      .catch(() => undefined);
  });
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
